import { FlagIcon, PlayIcon, TimerIcon, TrophyIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { ContrastTeamBadge } from "@/components/app/contrast-team-badge";
import { TeamChip } from "@/components/app/team-chip";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useTheme } from "@/lib/ground";
import { kitEdge, readableOn } from "@/lib/team-color";
import { cn } from "@/lib/utils";
import { liveFinish } from "../../../shared/live-moments";
import { kickoffCountdown, liveGameClock } from "../../../shared/live-view";
import {
  autoBanner,
  displayMs,
  formatClock,
  type GameState,
  type GameTeam,
  timeoutLeftMs,
} from "../../../shared/types";
import { useT } from "../../i18n";
import { dayHeading } from "../../i18n/format";
import { periodShort } from "../../i18n/labels";
import { useNow } from "../../lib/useGameState";
import { timeOf } from "./parts";
import { useScoringMoment } from "./useScoringMoment";

/** Each crest, full name and match indicators form one team identity,
 * facing the central score/clock. Names wrap inside their own column. */

/** One side of the hero. An undecided slot ("Winner SF 1") has words and
 *  nothing else: no code, no kit, no crest. */
export interface HeroSide {
  name: string;
  short?: string | undefined;
  color?: string | undefined;
  logo?: string | undefined;
  logoDark?: string | undefined;
}

function sideOfTeam(team: GameTeam): HeroSide {
  return {
    name: team.name,
    short: team.short,
    color: team.color,
    logo: team.logo,
    logoDark: team.logoDark,
  };
}

/** Team colours mark the edge; the match itself owns the quiet glass pane. */
function HeroShell({
  a,
  b,
  label,
  children,
}: {
  a: HeroSide;
  b: HeroSide;
  label: string;
  children: ReactNode;
}) {
  return (
    // Only the featured match floats; the timeline and other games stay flat.
    <Card
      role="group"
      aria-label={label}
      className="glass board-float gap-0 overflow-hidden p-0 ring-0"
    >
      <div aria-hidden="true" className="flex h-1 shrink-0">
        <span className="w-1/2 -skew-x-12" style={{ background: a.color }} />
        <span className="w-1/2 -skew-x-12" style={{ background: b.color }} />
      </div>
      {children}
    </Card>
  );
}

/** The crest, or a kit badge with the code, or a dashed ring for a side
 *  nobody has won yet. The dark theme asks for the crest's dark-ground variant
 *  first, the overlay's rule. */
function Crest({ side, dim = false }: { side: HeroSide; dim?: boolean }) {
  const theme = useTheme();
  const box = "size-14 sm:size-20";
  const [failed, setFailed] = useState<readonly string[]>([]);
  const variants = theme === "dark" ? [side.logoDark, side.logo] : [side.logo, side.logoDark];
  const logo = variants.find((value) => value && !failed.includes(value));
  if (side.short === undefined || side.color === undefined) {
    return (
      <span
        aria-hidden="true"
        className={cn(box, "shrink-0 rounded-full border border-dashed border-muted-foreground/50")}
      />
    );
  }
  return (
    <span
      className={cn(box, "inline-flex items-center justify-center", dim && "opacity-60")}
      onErrorCapture={() => {
        if (logo) setFailed((urls) => [...urls, logo]);
      }}
    >
      {logo ? (
        <ContrastTeamBadge
          logo={logo}
          color={side.color}
          short={side.short}
          className="size-full"
        />
      ) : (
        <TeamChip
          color={side.color}
          short={side.short}
          size="md"
          className="px-2 py-1.5 text-lg sm:px-3 sm:py-2 sm:text-2xl"
        />
      )}
    </span>
  );
}

/** Keep the name close to its crest, with the same centre on every screen. */
function Column({
  side,
  dim = false,
  children,
}: {
  side: HeroSide;
  dim?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col items-center gap-2 text-center">
      <Crest side={side} dim={dim} />
      <p
        className={cn(
          "nxt w-full text-sm leading-tight text-pretty [overflow-wrap:anywhere] min-[400px]:text-base sm:text-xl",
          dim && "text-muted-foreground",
        )}
      >
        {side.name}
      </p>
      {children}
    </div>
  );
}

/** What the game is, top right: the pitch when no pill above names it, then
 *  the round's own name or the division and pool. Not `.eyebrow`: at 0.3em of
 *  tracking «SECOND FIELD · WOMEN · POOL A» does not fit a phone beside the
 *  badge, and these are names, not a label. */
function Context({ parts }: { parts: string[] }) {
  if (parts.length === 0) return null;
  return (
    <span className="min-w-0 text-right text-xs leading-relaxed font-medium text-muted-foreground text-balance">
      {parts.join(" · ")}
    </span>
  );
}

/** The way to the stream. No field in the label: the pill above the hero
 *  already names the pitch, and on a one-pitch event so does the hero's own
 *  context line. `board.watchLive` keeps its `{field}` for the view's
 *  no-game branch, which offers several pitches at once. */
function Watch({ href }: { href: string }) {
  const t = useT();
  return (
    <Button
      variant="outline"
      className="w-full"
      render={<a href={href} target="_blank" rel="noopener noreferrer" />}
    >
      <PlayIcon className="text-brand-ink dark:text-brand" />
      {t("board.live.watch")}
    </Button>
  );
}

// ---------- a game on the pitch ----------

export function LiveHero({
  state,
  skew,
  connected,
  context,
  fieldName,
  streamUrl,
}: {
  state: GameState;
  skew: number;
  /** This pitch's own socket: the badge says so when the score has stopped
   *  arriving, instead of pulsing "live" over a number that is going stale. */
  connected: boolean;
  context: string[];
  fieldName: string;
  streamUrl?: string | undefined;
}) {
  const t = useT();
  // Once a second, like the pitch pill's: the clock prints seconds, and this
  // pane is glass over two kit lights, so twice a second was twice the
  // repaint for the same number.
  const now = useNow(skew, 1000);
  const moment = useScoringMoment(state, connected, skew);
  const finish = liveFinish(state, now);
  const liveClock = liveGameClock(state, now);
  const final = state.period === "final";
  const timeoutLeft =
    state.period === "h1" || state.period === "h2" ? timeoutLeftMs(state.timeout, now) : null;
  const timeoutTeam = state.timeout?.team === "A" ? state.teamA : state.teamB;
  const a = sideOfTeam(state.teamA);
  const b = sideOfTeam(state.teamB);
  const lostA = final && state.teamA.score < state.teamB.score;
  const lostB = final && state.teamB.score < state.teamA.score;
  const banner = final ? null : (state.banner ?? autoBanner(state));
  const bannerKeys = {
    "UNIVERSE POINT": "board.live.universePoint",
    "GAME POINT": "board.live.gamePoint",
    "HARD CAP": "board.info.hardCap",
    "SOFT CAP": "board.info.softCap",
  } as const;
  const bannerText =
    banner !== null && banner in bannerKeys
      ? t(bannerKeys[banner as keyof typeof bannerKeys])
      : banner;
  const winner = (
    <span className="inline-flex flex-wrap items-center justify-center gap-1 text-xs font-semibold text-brand-ink dark:text-brand">
      <TrophyIcon aria-hidden="true" className="size-3.5" />
      {t("board.live.winner")}
    </span>
  );

  return (
    <HeroShell a={a} b={b} label={`${fieldName}: ${a.name} – ${b.name}`}>
      {/* The strap: what state the game is in, and what the game is. The
          period rides OUTSIDE the badge as a plain microlabel - green is
          semantic here (README §1), so a tinted pill may say "this is
          happening now" and nothing else. A final wears no pill at all: the
          pane has already dropped the clock and dimmed the loser, and an
          unfilled outline around muted 10px type read as a greyed-out live
          badge rather than as a result. */}
      <div className="flex items-start justify-between gap-3 px-4 pt-3.5">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          {final ? (
            <span className="eyebrow text-muted-foreground">{periodShort("final")}</span>
          ) : (
            <>
              <span
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-semibold",
                  connected
                    ? "border-go/30 bg-go/10 text-go-ink"
                    : "border-warn/30 bg-warn/10 text-warn-ink",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "size-1.5 rounded-full",
                    connected ? "motion-safe:animate-pulse bg-go" : "bg-warn",
                  )}
                />
                {connected ? t("board.live.badge") : t("nav.reconnecting")}
              </span>
              <span className="text-xs text-muted-foreground">{periodShort(state.period)}</span>
            </>
          )}
        </span>
        <Context parts={context} />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-1 px-3 py-5 sm:gap-5 sm:px-8 sm:py-6">
        <Column side={a} dim={lostA}>
          {finish.winner === "A" && winner}
          {!final && <Meta team={state.teamA} side="A" state={state} />}
        </Column>
        <div className="flex flex-col items-center gap-2 self-center">
          <div className="nxt tabular flex items-center gap-1 text-4xl leading-none min-[400px]:text-5xl sm:gap-3 sm:text-7xl">
            <ScoreNumber
              score={state.teamA.score}
              dim={lostA}
              color={a.color}
              moment={moment?.team === "A" ? moment.at : null}
            />
            <span className="text-[0.5em] text-muted-foreground/60">:</span>
            <ScoreNumber
              score={state.teamB.score}
              dim={lostB}
              color={b.color}
              moment={moment?.team === "B" ? moment.at : null}
            />
          </div>
          {!final && (
            <div className="tabular flex flex-col items-center gap-1 leading-none">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5",
                  finish.urgent
                    ? "border-brand/50 bg-brand/10 text-brand-ink dark:text-brand"
                    : "bg-background/70",
                )}
              >
                <TimerIcon aria-hidden="true" className="size-3.5" />
                <span className="nxt text-base sm:text-xl">
                  {formatClock(displayMs(state, now)).padStart(5, "0")}
                </span>
              </span>
              <span className="min-h-4 text-xs font-semibold sm:text-sm">
                {liveClock.startsWith("+")
                  ? liveClock
                  : finish.urgent
                    ? t("board.live.finalMinutes")
                    : null}
              </span>
            </div>
          )}
        </div>
        <Column side={b} dim={lostB}>
          {finish.winner === "B" && winner}
          {!final && <Meta team={state.teamB} side="B" state={state} />}
        </Column>
      </div>
      <span className="sr-only" role="status">
        {moment ? t("board.live.goalFor", { team: moment.team === "A" ? a.name : b.name }) : null}
      </span>

      <div className="flex flex-col gap-3.5 px-4 empty:hidden [&:has(>*)]:pb-4">
        {timeoutLeft !== null && (
          <section
            aria-label={t("board.live.timeout", { team: timeoutTeam.name })}
            className="flex items-center gap-2.5 rounded-lg border bg-muted/60 px-3 py-2.5"
          >
            <TimerIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 text-sm font-semibold">
              {t("board.live.timeout", { team: timeoutTeam.name })}
            </span>
            {/* Like the keeper: round up the last partial second and remove
                the status at zero. The game clock keeps its own behaviour. */}
            <span role="timer" className="nxt tabular shrink-0 text-xl leading-none">
              {formatClock(Math.ceil(timeoutLeft / 1000) * 1000)}
            </span>
          </section>
        )}
        {streamUrl !== undefined && <Watch href={streamUrl} />}
      </div>

      {/* LAST, and full-bleed to the pane's own radius (the Card clips it):
          «ГЕЙМ-ПОІНТ» arrives mid-point, and anywhere higher it shoved
          everything under it down as the game reached its last point. */}
      {banner !== null && (
        <div
          key={banner}
          className={cn(
            "flex items-center justify-center gap-2 px-4 py-2.5 text-center",
            finish.point
              ? "bg-brand text-brand-on motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:duration-300"
              : "bg-muted text-foreground",
          )}
        >
          {finish.point && <FlagIcon aria-hidden="true" className="size-4 shrink-0" />}
          <span className="nxt text-sm sm:text-base">{bannerText}</span>
        </div>
      )}
    </HeroShell>
  );
}

function ScoreNumber({
  score,
  dim,
  color,
  moment,
}: {
  score: number;
  dim: boolean;
  color: string | undefined;
  moment: number | null;
}) {
  return (
    <span className={cn("relative isolate inline-flex", dim && "text-muted-foreground")}>
      {moment !== null && (
        <span
          key={`flash-${moment}`}
          aria-hidden="true"
          style={{ background: color }}
          className="pointer-events-none absolute -inset-x-2 inset-y-0 -z-10 rounded-lg opacity-25 motion-reduce:hidden motion-safe:animate-out motion-safe:fade-out-0 motion-safe:duration-1000 motion-safe:fill-mode-forwards"
        />
      )}
      <span
        key={moment ?? "settled"}
        data-scoring={moment !== null || undefined}
        className={cn(
          moment !== null &&
            "motion-safe:animate-in motion-safe:zoom-in-125 motion-safe:duration-500",
        )}
      >
        {score}
      </span>
    </span>
  );
}

/**
 * Timeouts and possession, under the crest they belong to.
 *
 * It was a row of its own across the pane, with the period and the clock
 * between the two sides; under each crest it costs no row at all, and the
 * question "who has the disc" is answered where the eye already is. The slot
 * keeps its height whether or not the chip is in it, so the pair does not
 * jump when the disc changes hands.
 *
 * Kept, against a proposal to cut it: a spectator standing at ANOTHER pitch,
 * and the reader following from home in the Mini App, cannot see who is on
 * offence; it is the one thing here that is not on the field in front of them.
 */
function Meta({ team, side, state }: { team: GameTeam; side: "A" | "B"; state: GameState }) {
  const t = useT();
  const theme = useTheme();
  const total = state.config.timeoutsPerHalf;
  const onOffense = state.offense === side;
  const ink = readableOn(team.color);
  if (total === 0 && !onOffense) return <span className="min-h-[1.375rem]" />;
  return (
    <span className="flex min-h-[1.375rem] flex-wrap items-center justify-center gap-1.5">
      {total > 0 && (
        <span
          role="img"
          aria-label={t("board.live.timeouts", { left: team.timeoutsLeft, total })}
          className="flex items-center gap-1"
        >
          {Array.from({ length: total }, (_, i) => `timeout-${i}`).map((key, i) => (
            <span
              key={key}
              className={cn(
                "lean h-1.5 w-4 rounded-xs",
                i < team.timeoutsLeft ? "bg-foreground/70" : "bg-foreground/15",
              )}
            />
          ))}
        </span>
      )}
      {onOffense && (
        <span
          data-edge={kitEdge(team.color, theme)}
          className="slant-chip px-2 py-1 text-[0.625rem] font-bold leading-none tracking-wide"
          style={{ background: ink.bg, color: ink.fg }}
        >
          {t("board.offense")}
        </span>
      )}
    </span>
  );
}

// ---------- nothing on the pitch yet ----------

/**
 * The pitch's next game, where its score will be: the same two crests, the
 * kickoff where the score goes, and a countdown that ticks locally the way
 * every clock here does (no broadcast, rule 5). Today only - a kickoff on
 * another day says the day instead; nobody counts eighteen hours down.
 */
export function NextHero({
  a,
  b,
  startAt,
  today,
  context,
  fieldName,
  streamUrl,
}: {
  a: HeroSide;
  b: HeroSide;
  startAt: string;
  today: string;
  context: string[];
  fieldName: string;
  streamUrl?: string | undefined;
}) {
  const t = useT();
  const now = useNow(0, 1000);
  const day = startAt.slice(0, 10);
  const countdown = day === today ? kickoffCountdown(startAt, now) : null;

  return (
    <HeroShell a={a} b={b} label={`${fieldName}: ${a.name} – ${b.name}`}>
      <div className="flex items-start justify-between gap-3 px-4 pt-3.5">
        <span className="eyebrow py-1.5 text-muted-foreground">{t("board.live.nextGame")}</span>
        <Context parts={context} />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-1 px-3 pt-5 pb-2 sm:gap-5 sm:px-8 sm:pt-6">
        <Column side={a} />
        <div className="flex flex-col items-center gap-2 self-center">
          {day !== today && (
            <span className="eyebrow text-muted-foreground">{dayHeading(day)}</span>
          )}
          <span className="nxt tabular text-3xl leading-none text-brand-ink min-[400px]:text-4xl sm:text-5xl adm:text-7xl dark:text-brand">
            {timeOf(startAt)}
          </span>
        </div>
        <Column side={b} />
      </div>

      <div className="flex flex-col gap-3.5 px-4 pt-3.5 pb-4">
        {day === today && (
          <div className="flex flex-col items-center gap-2">
            {countdown !== null && (
              <span className="eyebrow text-muted-foreground">{t("board.live.untilStart")}</span>
            )}
            <span className="nxt tabular text-3xl leading-none adm:text-4xl">
              {countdown ?? t("board.live.startingNow")}
            </span>
          </div>
        )}
        {streamUrl !== undefined && <Watch href={streamUrl} />}
      </div>
    </HeroShell>
  );
}
